import { ref, computed } from 'vue';
import globalAxios from 'axios';
import {
  startRegistration,
  startAuthentication,
  browserSupportsWebAuthn,
} from '@simplewebauthn/browser';

function getErrorMessage(error) {
  if (error instanceof Error) {
    if (error.name === 'NotAllowedError') return 'PASSKEY_OPERATION_CANCELLED';
    if (error.name === 'InvalidStateError') return 'PASSKEY_ALREADY_REGISTERED';
    if (error.name === 'NotSupportedError')
      return 'PASSKEY_AUTHENTICATOR_NOT_SUPPORTED';
    if (error.name === 'SecurityError') return 'PASSKEY_SECURITY_ERROR';
    if (error.name === 'AbortError') return 'PASSKEY_OPERATION_ABORTED';
    return error.message;
  }
  return 'PASSKEY_UNKNOWN_ERROR';
}

function getAuthHeaders() {
  try {
    const cookieMatch = document.cookie.match(/cw_d_session_info=([^;]+)/);
    if (!cookieMatch) return {};
    const decoded = decodeURIComponent(cookieMatch[1]);
    const data = JSON.parse(decoded);
    return {
      'access-token': data['access-token'],
      'token-type': data['token-type'] || 'Bearer',
      client: data.client,
      expiry: data.expiry,
      uid: data.uid,
    };
  } catch (e) {
    return {};
  }
}

function updateAuthCookie(response) {
  try {
    const headers = response.headers;
    const accessToken = headers['access-token'];
    const client = headers.client;
    const uid = headers.uid;
    const expiry = headers.expiry;

    if (accessToken && client && uid) {
      const cookieMatch = document.cookie.match(/cw_d_session_info=([^;]+)/);
      let existingData = {};
      if (cookieMatch) {
        existingData = JSON.parse(decodeURIComponent(cookieMatch[1]));
      }

      const updatedData = {
        ...existingData,
        'access-token': accessToken,
        client,
        uid,
        expiry: expiry || existingData.expiry,
        'token-type':
          headers['token-type'] || existingData['token-type'] || 'Bearer',
      };

      document.cookie = `cw_d_session_info=${encodeURIComponent(JSON.stringify(updatedData))}; path=/; SameSite=Lax`;
    }
  } catch (e) {
    // Silent fail
  }
}

export function usePasskeyAuth() {
  const axios = window.axios || globalAxios;
  const status = ref('idle');
  const error = ref(null);

  const isSupported = browserSupportsWebAuthn();

  const register = async deviceName => {
    if (!isSupported) {
      error.value = {
        code: 'NOT_SUPPORTED',
        message: 'PASSKEY_NOT_SUPPORTED',
      };
      return { success: false };
    }

    status.value = 'loading';
    error.value = null;

    try {
      // 1. Inicia o registro no backend para obter opções do WebAuthn
      const responseOptions = await axios.post(
        '/api/v1/auth/passkey/register/options',
        {},
        { headers: getAuthHeaders() }
      );
      updateAuthCookie(responseOptions);

      // 2. Chama a API do browser para o leitor biométrico
      const credential = await startRegistration({
        optionsJSON: responseOptions.data,
      });

      // 3. Envia o resultado de volta ao backend para verificação
      const verifyResponse = await axios.post(
        '/api/v1/auth/passkey/register/verify',
        {
          deviceName,
          response: credential,
        },
        { headers: getAuthHeaders() }
      );
      updateAuthCookie(verifyResponse);

      status.value = 'success';
      return verifyResponse.data;
    } catch (err) {
      const errMsg = err.response?.data?.error || getErrorMessage(err);
      error.value = { code: 'REGISTRATION_FAILED', message: errMsg };
      status.value = 'error';
      return { success: false };
    }
  };

  const login = async email => {
    if (!isSupported) {
      error.value = {
        code: 'NOT_SUPPORTED',
        message: 'PASSKEY_NOT_SUPPORTED',
      };
      return null;
    }

    status.value = 'loading';
    error.value = null;

    try {
      // 1. Inicia o desafio de login no backend enviando o e-mail
      const responseOptions = await axios.post(
        '/api/v1/auth/passkey/login/options',
        { email }
      );
      const { challengeToken, ...optionsJSON } = responseOptions.data;

      // 2. Chama o leitor biométrico do browser
      const credential = await startAuthentication({
        optionsJSON,
      });

      // 3. Envia a assinatura digital de volta para verificação e login
      const verifyResponse = await axios.post(
        '/api/v1/auth/passkey/login/verify',
        {
          challengeToken,
          response: credential,
        }
      );

      status.value = 'success';
      return verifyResponse;
    } catch (err) {
      const errMsg = err.response?.data?.error || getErrorMessage(err);
      error.value = { code: 'AUTHENTICATION_FAILED', message: errMsg };
      status.value = 'error';
      return null;
    }
  };

  const listCredentials = async () => {
    try {
      const response = await axios.get('/api/v1/auth/passkey/list', {
        headers: getAuthHeaders(),
      });
      updateAuthCookie(response);
      return response.data;
    } catch (err) {
      return [];
    }
  };

  const removeCredential = async credentialId => {
    try {
      const response = await axios.delete(
        `/api/v1/auth/passkey/${encodeURIComponent(credentialId)}`,
        { headers: getAuthHeaders() }
      );
      updateAuthCookie(response);
      return true;
    } catch (err) {
      return false;
    }
  };

  const reset = () => {
    status.value = 'idle';
    error.value = null;
  };

  return {
    status,
    error,
    isSupported,
    isLoading: computed(() => status.value === 'loading'),
    register,
    login,
    listCredentials,
    removeCredential,
    reset,
  };
}
